import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../utils/reportsApi';
import { api } from '../../utils/api';
import './ReportCommon.css';

function fmt(n) {
  if (!n && n !== 0) return '';
  const v = Number(n);
  if (v === 0) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TrialBalancePage = () => {
  const navigate = useNavigate();

  // filter state
  const [periodId,  setPeriodId]  = useState('');
  const [entityId,  setEntityId]  = useState('');
  const [view,      setView]      = useState('detail');

  // meta
  const [periods,   setPeriods]   = useState([]);
  const [entities,  setEntities]  = useState([]);

  // result
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/fiscal/fiscal-years'),
      api.get('/entities'),
    ]).then(([fys, ents]) => {
      const allPeriods = (fys || []).flatMap((fy) =>
        (fy.periods || []).map((p) => ({ ...p, fyCode: fy.code }))
      );
      allPeriods.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setPeriods(allPeriods);
      setEntities(ents || []);
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!periodId) { setError('Select a period.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const data = await reportsApi.getTrialBalance({ periodId, entityId: entityId || undefined, view });
      setResult(data);
    } catch (e) {
      setError(e.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const exportXlsx = async () => {
    if (!periodId) return;
    setExporting(true);
    try {
      await reportsApi.exportTrialBalance(
        { periodId, entityId: entityId || undefined, view },
        `trial-balance-${result?.period?.code ?? 'report'}.xlsx`
      );
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const lines  = result?.lines  ?? [];
  const totals = result?.totals ?? {};

  return (
    <div className="rpt-page">
      <div className="rpt-header">
        <button className="rpt-back" onClick={() => navigate('/reports')}>
          <i className="fas fa-arrow-left"></i> Reports
        </button>
        <h1 className="rpt-title">Trial Balance</h1>
      </div>

      <div className="rpt-filters">
        <label className="rpt-label">
          Period <span className="rpt-req">*</span>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="rpt-select">
            <option value="">— select period —</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{p.fyCode} · {p.name}</option>
            ))}
          </select>
        </label>
        <label className="rpt-label">
          Entity
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="rpt-select">
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <label className="rpt-label">
          View
          <select value={view} onChange={(e) => setView(e.target.value)} className="rpt-select">
            <option value="detail">Detail (posting accounts)</option>
            <option value="totals">Totals (header groups)</option>
          </select>
        </label>
        <div className="rpt-filter-actions">
          <button className="rpt-btn rpt-btn--primary" onClick={run} disabled={loading || !periodId}>
            {loading ? <><i className="fas fa-spinner fa-spin"></i> Running…</> : <><i className="fas fa-play"></i> Run</>}
          </button>
          {result && (
            <button className="rpt-btn rpt-btn--excel" onClick={exportXlsx} disabled={exporting}>
              {exporting ? <><i className="fas fa-spinner fa-spin"></i> Exporting…</> : <><i className="fas fa-file-excel"></i> Export Excel</>}
            </button>
          )}
        </div>
      </div>

      {error && <div className="rpt-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {result && (
        <div className="rpt-result">
          <div className="rpt-meta">
            Period: <strong>{result.period?.name}</strong>
            {result.entity && <> &nbsp;·&nbsp; Entity: <strong>{result.entity.name}</strong></>}
            &nbsp;·&nbsp; {lines.length} row{lines.length !== 1 ? 's' : ''}
          </div>
          <div className="rpt-table-wrap">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th className="col-code">Code</th>
                  <th className="col-name">Name</th>
                  <th className="col-amt">Opening Dr</th>
                  <th className="col-amt">Opening Cr</th>
                  <th className="col-amt">Movement Dr</th>
                  <th className="col-amt">Movement Cr</th>
                  <th className="col-amt">Closing Dr</th>
                  <th className="col-amt">Closing Cr</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr><td colSpan="8" className="rpt-empty">No accounts with activity in this period.</td></tr>
                )}
                {lines.map((r) => (
                  <tr key={r.accountId} className={r.type === 'HEADER' ? 'row-header' : ''}>
                    <td className="col-code" style={{ paddingLeft: `${(r.level || 0) * 16 + 8}px` }}>{r.code}</td>
                    <td className="col-name">{r.name}</td>
                    <td className="col-amt">{fmt(r.openingDebit)}</td>
                    <td className="col-amt">{fmt(r.openingCredit)}</td>
                    <td className="col-amt">{fmt(r.movementDebit)}</td>
                    <td className="col-amt">{fmt(r.movementCredit)}</td>
                    <td className="col-amt">{fmt(r.closingDebit)}</td>
                    <td className="col-amt">{fmt(r.closingCredit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="row-totals">
                  <td colSpan="2">TOTALS</td>
                  <td className="col-amt">{fmt(totals.openingDebit)}</td>
                  <td className="col-amt">{fmt(totals.openingCredit)}</td>
                  <td className="col-amt">{fmt(totals.movementDebit)}</td>
                  <td className="col-amt">{fmt(totals.movementCredit)}</td>
                  <td className="col-amt">{fmt(totals.closingDebit)}</td>
                  <td className="col-amt">{fmt(totals.closingCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialBalancePage;
